// Local storage utilities for curves
import { Curve } from '../types';
import { syncInflationConfig } from '../inflation-curves';

const CURVES_KEY = 'quantra_curves';

export function generateId(): string {
  return `curve_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getSavedCurves(): Curve[] {
  try {
    const data = localStorage.getItem(CURVES_KEY);
    if (!data) return [];
    const curves = JSON.parse(data);
    // Migrate old curves without role
    return curves.map((c: any) => ({
      ...c,
      role: c.role || 'discount',
      inflation_curve: c.inflation_curve ? syncInflationConfig(c.inflation_curve, c.currency || 'EUR') : undefined,
    }));
  } catch {
    return [];
  }
}

export function saveCurve(curve: Curve): Curve {
  const curves = getSavedCurves();
  const idx = curves.findIndex(c => c.id === curve.id);
  const now = new Date().toISOString();
  const updated = { ...curve, updatedAt: now, createdAt: curve.createdAt || now };
  if (idx >= 0) curves[idx] = updated; else curves.push(updated);
  localStorage.setItem(CURVES_KEY, JSON.stringify(curves));
  return updated;
}

export function deleteCurve(curveId: string): void {
  const curves = getSavedCurves().filter(c => c.id !== curveId);
  localStorage.setItem(CURVES_KEY, JSON.stringify(curves));
}

export function getCurveById(curveId: string): Curve | null {
  return getSavedCurves().find(c => c.id === curveId) || null;
}

export function exportCurves(curves: Curve[]): void {
  const blob = new Blob([JSON.stringify(curves, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `quantra-curves-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportCurve(curve: Curve): void {
  const blob = new Blob([JSON.stringify(curve, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${curve.name.toLowerCase().replace(/\s+/g, '-')}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function importCurves(file: File): Promise<Curve[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        const curves: Curve[] = (Array.isArray(parsed) ? parsed : [parsed]).map((c: any) => ({
          ...c,
          id: c.id || generateId(),
          role: c.role || 'discount',
          inflation_curve: c.inflation_curve ? syncInflationConfig(c.inflation_curve, c.currency || 'EUR') : undefined,
          createdAt: c.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
        const existing = getSavedCurves();
        const merged = [...existing];
        for (const curve of curves) {
          const idx = merged.findIndex(c => c.id === curve.id);
          if (idx >= 0) merged[idx] = curve; else merged.push(curve);
        }
        localStorage.setItem(CURVES_KEY, JSON.stringify(merged));
        resolve(curves);
      } catch {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export function curveToApiFormat(curve: Curve): object {
  return {
    id: curve.id,
    day_counter: curve.day_counter,
    interpolator: curve.interpolator,
    reference_date: curve.reference_date,
    bootstrap_trait: curve.bootstrap_trait,
    points: curve.points,
  };
}
